import React, { useState } from 'react';
import { FEATURE_PERMISSIONS, hasPermission } from '../utils/permissions';
import Toast from '../components/common/Toast';
import LogCleanupTab from '../components/logs/LogCleanupTab';
import LogViewerTab from '../components/logs/LogViewerTab';
import LogAnalyticsTab from '../components/logs/LogAnalyticsTab';
import LogAlertsTab from '../components/logs/LogAlertsTab';
import LogExportTab from '../components/logs/LogExportTab';

const TABS = [
  { key: 'viewer', label: '📊 Log Viewer', icon: '📊' },
  { key: 'analytics', label: '📈 Analytics', icon: '📈' },
  { key: 'alerts', label: '🔔 Alerts', icon: '🔔' },
  { key: 'export', label: '📤 Export', icon: '📤' },
  { key: 'cleanup', label: '🗑️ Cleanup', icon: '🗑️' },
];

const LogManagementPage = ({ user }) => {
  const [activeTab, setActiveTab] = useState('viewer');
  const [toast, setToast] = useState(null);
  
  // Check permission
  const canManageLogs = hasPermission(user, FEATURE_PERMISSIONS.LOGS_MANAGE) || hasPermission(user, FEATURE_PERMISSIONS.LOGS_VIEW);
  const isReadOnly = !hasPermission(user, FEATURE_PERMISSIONS.LOGS_MANAGE);
  
  // Permission check
  if (!canManageLogs) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">Access Denied</p>
          <p className="text-red-600 text-sm mt-1">
            You don't have permission to manage logs. This feature is admin-only.
          </p>
        </div>
      </main>
    );
  }
  
  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">📋 Log Management</h2>
        <p className="text-gray-600">
          View, analyze, and manage system logs. Configure alerts and export data for compliance.
        </p>
      </div>
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'viewer' && <LogViewerTab user={user} />}
      {activeTab === 'analytics' && <LogAnalyticsTab user={user} />}
      {activeTab === 'alerts' && <LogAlertsTab user={user} isReadOnly={isReadOnly} setToast={setToast} />}
      {activeTab === 'export' && <LogExportTab user={user} setToast={setToast} />}
      {activeTab === 'cleanup' && <LogCleanupTab user={user} isReadOnly={isReadOnly} setToast={setToast} />}
    </main>
  );
};

export default LogManagementPage;
